import { QueryResult, useQuery } from "@apollo/client"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  InfluencerSortFieldEnum,
  InfluencerTableFragment,
  RosterAccessModeEnum,
  SortDirectionEnum,
} from "~/__generated__/graphql"
import {
  CASTING_CALL_SUBMISSIONS_QUERY,
  INFLUENCER_TABLE_FRAGMENT,
  ROSTER_DATABASE_QUERY_DOCUMENT,
} from "./api"
import { SearchValues, searchValuesToGraphql } from "./schema"
import { getFragmentData } from "~/__generated__"
import { ResultOf, VariablesOf } from "@graphql-typed-document-node/core"
import { trackEvent } from "~/util/tracking"

const defaultSorts: Record<InfluencerSortFieldEnum, SortDirectionEnum> = {
  [InfluencerSortFieldEnum.Name]: SortDirectionEnum.Asc,
  [InfluencerSortFieldEnum.AgencyName]: SortDirectionEnum.Asc,
  [InfluencerSortFieldEnum.AgentEmail]: SortDirectionEnum.Asc,
  [InfluencerSortFieldEnum.Category]: SortDirectionEnum.Asc,
  [InfluencerSortFieldEnum.Country]: SortDirectionEnum.Asc,
  [InfluencerSortFieldEnum.Exclusivity]: SortDirectionEnum.Asc,
  [InfluencerSortFieldEnum.TikTokFollowers]: SortDirectionEnum.Desc,
  [InfluencerSortFieldEnum.InstagramFollowers]: SortDirectionEnum.Desc,
  [InfluencerSortFieldEnum.YoutubeSubscribers]: SortDirectionEnum.Desc,
  [InfluencerSortFieldEnum.TwitchSubscribers]: SortDirectionEnum.Desc,
}

const DEFAULT_SORT_FIELD = InfluencerSortFieldEnum.Category

// Reports a free-text roster search once its result count is known. The search
// runs on every keystroke, so the query is only reported after the matching
// query resolves.
const useTrackSearch = ({
  query,
  loading,
  resultsCount,
}: {
  query?: string
  loading: boolean
  resultsCount?: number
}) => {
  const trackedQuery = useRef<string | null>(null)

  useEffect(() => {
    if (!query) {
      trackedQuery.current = null
      return
    }
    if (loading || resultsCount === undefined) return
    if (trackedQuery.current === query) return

    trackedQuery.current = query
    trackEvent("search_influencers", { query, results_count: resultsCount })
  }, [query, loading, resultsCount])
}

type UseInfluencersResultBase = {
  influencers: Array<InfluencerTableFragment>
  isFiltering: boolean
  loading: boolean
  error?: Error
  setSearchValues: (values: SearchValues) => void
  sortField?: InfluencerSortFieldEnum
  sortDir: SortDirectionEnum
  onSort: (field: InfluencerSortFieldEnum) => void
  onLoadNextPage: () => void
  pageInfo?:
    | {
        hasNextPage: boolean
        endCursor?: string | null
      }
    | null
    | undefined
}

type RosterQueryResult = QueryResult<
  ResultOf<typeof ROSTER_DATABASE_QUERY_DOCUMENT>,
  VariablesOf<typeof ROSTER_DATABASE_QUERY_DOCUMENT>
>

export type UseInfluencersResult = UseInfluencersResultBase &
  (
    | RosterQueryResult
    | QueryResult<ResultOf<typeof CASTING_CALL_SUBMISSIONS_QUERY>>
  )

// Which roster to read. The server authorizes each mode separately, so the
// arguments that belong to one mode can't be passed with another.
export type RosterAccess =
  | {
      mode: RosterAccessModeEnum.OwnRoster
      castingCallId?: string
      publicAgencyId?: never
    }
  | {
      mode: RosterAccessModeEnum.PublicRoster
      publicAgencyId: string
      castingCallId?: never
    }
  | {
      mode: RosterAccessModeEnum.MarketplaceDiscovery
      publicAgencyId?: never
      castingCallId?: never
    }

export const useInfluencers = ({
  access,
  filters,
  skip,
}: {
  // null while the screen is still resolving which roster it shows (e.g. the
  // public page loading its agency); the query is skipped until then.
  access: RosterAccess | null
  filters?: SearchValues
  skip?: boolean
}): UseInfluencersResultBase & RosterQueryResult => {
  const [lastSearchField, setLastSearchField] = useState<string | undefined>()
  const [searchValues, setSearchValues] = useState<SearchValues>({})
  const [sortField, setSortField] = useState<
    InfluencerSortFieldEnum | undefined
  >(DEFAULT_SORT_FIELD)
  const [sortDir, setSortDir] = useState<SortDirectionEnum>(
    SortDirectionEnum.Asc
  )

  // Filtering by a query string uses its own ordering by default based on match quality,
  // so let's clear our custom one. Clicking a column again will reintroduce a custom order.
  useEffect(() => {
    if (searchValues.query !== lastSearchField) {
      if (searchValues.query) {
        setSortField(undefined)
      } else {
        setSortField(DEFAULT_SORT_FIELD)
        setSortDir(defaultSorts[DEFAULT_SORT_FIELD])
      }
      setLastSearchField(searchValues.query)
    }
  }, [lastSearchField, searchValues.query])

  const queryResult = useQuery(ROSTER_DATABASE_QUERY_DOCUMENT, {
    variables: {
      // Never sent while access is null: the query is skipped below.
      mode: access?.mode ?? RosterAccessModeEnum.MarketplaceDiscovery,
      publicAgencyId: access?.publicAgencyId,
      castingCallId: access?.castingCallId,
      filters: searchValuesToGraphql({ ...searchValues, ...filters }),
      sortField: sortField,
      sortDirection: sortDir,
      first: 50,
      influencersCursor: null,
    },
    skip: skip || access === null,
    notifyOnNetworkStatusChange: true,
  })

  const influencers =
    queryResult.data?.influencers.edges.map((e) =>
      getFragmentData(INFLUENCER_TABLE_FRAGMENT, e.node)
    ) ?? []

  useTrackSearch({
    query: searchValues.query,
    loading: queryResult.loading,
    resultsCount: queryResult.data?.influencers.totalCount,
  })

  const onSort = (field: InfluencerSortFieldEnum) => {
    const direction =
      field === sortField
        ? sortDir === SortDirectionEnum.Asc
          ? SortDirectionEnum.Desc
          : SortDirectionEnum.Asc
        : defaultSorts[field]

    setSortField(field)
    setSortDir(direction)
    trackEvent("sort_influencers", {
      sort_field: field,
      sort_direction: direction,
    })
  }

  const onLoadNextPage = () => {
    queryResult.fetchMore({
      variables: {
        influencersCursor: queryResult?.data?.influencers.pageInfo.endCursor,
      },
    })
  }

  const isFiltering = useMemo(() => {
    return Object.values(searchValues).some((v) => v)
  }, [searchValues])

  return {
    ...queryResult,
    influencers,
    isFiltering,
    setSearchValues,
    sortField,
    sortDir,
    onSort,
    onLoadNextPage,
    pageInfo: queryResult?.data?.influencers?.pageInfo,
  }
}

export const useCastingCallInfluencers = (
  castingCallId: string
): UseInfluencersResultBase &
  QueryResult<ResultOf<typeof CASTING_CALL_SUBMISSIONS_QUERY>, any> => {
  const [lastSearchField, setLastSearchField] = useState<string | undefined>()
  const [searchValues, setSearchValues] = useState<SearchValues>({})
  const [sortField, setSortField] = useState<
    InfluencerSortFieldEnum | undefined
  >(DEFAULT_SORT_FIELD)
  const [sortDir, setSortDir] = useState<SortDirectionEnum>(
    defaultSorts[DEFAULT_SORT_FIELD]
  )

  // Filtering by a query string uses its own ordering by default based on match quality,
  // so let's clear our custom one. Clicking a column again will reintroduce a custom order.
  useEffect(() => {
    if (searchValues.query !== lastSearchField) {
      if (searchValues.query) {
        setSortField(undefined)
      } else {
        setSortField(DEFAULT_SORT_FIELD)
        setSortDir(defaultSorts[DEFAULT_SORT_FIELD])
      }
      setLastSearchField(searchValues.query)
    }
  }, [lastSearchField, searchValues.query])

  const queryResult = useQuery(CASTING_CALL_SUBMISSIONS_QUERY, {
    variables: {
      castingCallId,
      filters: searchValuesToGraphql(searchValues),
      sortField,
      sortDirection: sortDir,
      first: 50,
      influencersCursor: null,
    },
    notifyOnNetworkStatusChange: true,
  })

  const castingCall = queryResult.data?.castingCall

  const influencers =
    castingCall?.influencers?.edges.map((e) =>
      getFragmentData(INFLUENCER_TABLE_FRAGMENT, e.node)
    ) ?? []

  useTrackSearch({
    query: searchValues.query,
    loading: queryResult.loading,
    resultsCount: castingCall?.influencers?.totalCount,
  })

  const onSort = (field: InfluencerSortFieldEnum) => {
    const direction =
      field === sortField
        ? sortDir === SortDirectionEnum.Asc
          ? SortDirectionEnum.Desc
          : SortDirectionEnum.Asc
        : defaultSorts[field]

    setSortField(field)
    setSortDir(direction)
    trackEvent("sort_influencers", {
      sort_field: field,
      sort_direction: direction,
    })
  }

  const onLoadNextPage = () => {
    queryResult.fetchMore({
      variables: {
        influencersCursor:
          queryResult?.data?.castingCall?.influencers?.pageInfo.endCursor,
      },
    })
  }

  const isFiltering = useMemo(() => {
    return Object.values(searchValues).some((v) => v)
  }, [searchValues])

  return {
    ...queryResult,
    influencers,
    isFiltering,
    setSearchValues,
    sortField,
    sortDir,
    onSort,
    onLoadNextPage,
    pageInfo: queryResult?.data?.castingCall.influencers?.pageInfo,
  }
}
